<template>
  <FilterSelectorItem
    :label="filterLabel"
    :options="userNeedFilter.options"
    :variant="variant"
    :icon-left="selectedUserNeedsIcon">
    <template #default="props">
      <ul class="max-h-80 overflow-y-auto" data-testid="menu-option-list">
        <UiListItem
          v-for="(option, index) in props.options"
          :key="option.value"
          class="hover:bg-neutral-whisper focus:bg-neutral-faint"
          :icon-name="iconMap.get(option.value as UserNeed)"
          :is-checked="option.selected"
          :checkbox-appearance="props.checkboxAppearance"
          :list-variant="props.listVariant"
          :check-box-menu="true"
          :class="{
            'border-t': option.hasDividerAbove,
          }"
          :data-testid="`menu-option-button-${index}`"
          @list-item-click="handleFilterChange(option.value)">
          <div class="flex flex-col gap-y-0.5">
            <div>{{ option.label }}</div>
            <div class="text-xs text-neutral-muted">
              {{ explanationTextMap.get(option.value) }}
            </div>
          </div>
        </UiListItem>
      </ul>
    </template>
  </FilterSelectorItem>
</template>

<script setup lang="ts">
import UiListItem from "@dpa-id-components/dpa-shared-components/src/components/UiListItem/UiListItem.vue";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import useFilterItem from "@/composition/archive/useFilterItem.ts";
import useFilterSelectorStore from "@/stores/search/filterSelector.ts";
import type { UserNeed } from "@/types/ui.ts";
import { iconMap } from "@/utils/content.ts";

import FilterSelectorItem from "./FilterSelectorItem.vue";
import type { FilterSelectorItemWrapperProps } from "./filterSelectorItemWrapper";

const { t } = useI18n();

defineProps<FilterSelectorItemWrapperProps>();

const filterSelectorStore = useFilterSelectorStore();
const { userNeedFilter, selectedUserNeeds } = storeToRefs(filterSelectorStore);
const { handleFilterChange, filterLabel } = useFilterItem(
  userNeedFilter,
  selectedUserNeeds,
);

const selectedUserNeedsIcon = computed(() => {
  if (selectedUserNeeds.value.length > 1) {
    return undefined;
  }
  const userNeed = selectedUserNeeds.value[0] as UserNeed;
  return iconMap.get(userNeed);
});

const explanationTextMap = new Map<string, string>([
  ["dpauserneed:updateme", t("filters.filterUserNeedExplanation.updateMe")],
  [
    "dpauserneed:givemeperspective",
    t("filters.filterUserNeedExplanation.giveMePerspective"),
  ],
  ["dpauserneed:educateme", t("filters.filterUserNeedExplanation.educateMe")],
  ["dpauserneed:helpme", t("filters.filterUserNeedExplanation.helpMe")],
  ["dpauserneed:inspireme", t("filters.filterUserNeedExplanation.inspireMe")],
  ["dpauserneed:touchme", t("filters.filterUserNeedExplanation.touchMe")],
  ["dpauserneed:divertme", t("filters.filterUserNeedExplanation.divertMe")],
]);
</script>
